import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import {
  type CreateLocationRequest,
  CreateLocationRequestSchema,
  DEFAULT_CURRENCY,
  DEFAULT_TIMEZONE,
  UpdateLocationRequestSchema,
} from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { CURRENCY_OPTIONS, NICHE_OPTIONS, TIMEZONE_OPTIONS } from '../constants';

// ── Types ──────────────────────────────────────────────────────────────────

interface LocationFormProps {
  mode: 'create' | 'edit';
  onSubmit: (values: CreateLocationRequest) => Promise<void>;
  defaultValues?: Partial<CreateLocationRequest>;
  submitLabel?: string;
  onCancel?: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export function LocationForm({
  mode,
  onSubmit,
  defaultValues,
  submitLabel,
  onCancel,
}: LocationFormProps) {
  const { t } = useTranslation('locations');
  const schema = mode === 'create' ? CreateLocationRequestSchema : UpdateLocationRequestSchema;
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateLocationRequest>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      address: '',
      timezone: DEFAULT_TIMEZONE,
      currency: DEFAULT_CURRENCY,
      niche: 'restaurant',
      vehicleCapacity: null,
      ...defaultValues,
    },
  });

  async function handleFormSubmit(values: CreateLocationRequest) {
    setServerError(null);
    try {
      await onSubmit(values);
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('errors.generic') }));
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {serverError && <FormBanner message={serverError} />}

      <div className="grid gap-6 md:grid-cols-2">
        <Field label={t('form.name.label')} htmlFor="name" error={errors.name?.message}>
          <Input
            id="name"
            placeholder={t('form.name.placeholder')}
            disabled={isSubmitting}
            {...register('name')}
          />
        </Field>

        <Field label={t('form.address.label')} htmlFor="address" error={errors.address?.message}>
          <Input
            id="address"
            placeholder={t('form.address.placeholder')}
            disabled={isSubmitting}
            {...register('address')}
          />
        </Field>

        <Field label={t('form.timezone.label')} htmlFor="timezone" error={errors.timezone?.message}>
          <SelectInput id="timezone" disabled={isSubmitting} {...register('timezone')}>
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field label={t('form.currency.label')} htmlFor="currency" error={errors.currency?.message}>
          <SelectInput id="currency" disabled={isSubmitting} {...register('currency')}>
            {CURRENCY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field label={t('form.niche.label')} htmlFor="niche" error={errors.niche?.message}>
          <SelectInput id="niche" disabled={isSubmitting} {...register('niche')}>
            {NICHE_OPTIONS.map((n) => (
              <option key={n.value} value={n.value}>
                {enumLabel(t, 'niche', n.value)}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('form.vehicleCapacity.label')}
          htmlFor="vehicleCapacity"
          error={errors.vehicleCapacity?.message}
        >
          <Input
            id="vehicleCapacity"
            type="number"
            min={0}
            placeholder={t('form.vehicleCapacity.placeholder')}
            disabled={isSubmitting}
            {...register('vehicleCapacity', {
              setValueAs: (v: string) => (v === '' ? null : Number(v)),
            })}
          />
          <p className="mt-1 text-[12px] text-jq-graphite">{t('form.vehicleCapacity.hint')}</p>
        </Field>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('common:actions.cancel')}
          </Button>
        )}
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? t('form.saving') : (submitLabel ?? t('common:actions.save'))}
        </Button>
      </div>
    </form>
  );
}

// ── Building blocks ────────────────────────────────────────────────────────

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

const SelectInput = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function SelectInput({ className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        {...rest}
        className={`flex h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50 ${
          className ?? ''
        }`}
      />
    );
  },
);

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
